import { useRef } from 'react';
import { Mic, MicOff, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import Button from '../ui/Button';
import { useSpeechRecognition } from '../../hooks/useSpeech';

/**
 * Prompt textarea with optional voice input + a primary action button.
 * Ctrl/Cmd+Enter submits.
 */
export default function PromptInput({
  value,
  onChange,
  onSubmit,
  loading = false,
  placeholder = 'Describe what you want to create…',
  actionLabel = 'Generate',
  rows = 4,
  voice = true,
  disabled = false,
}) {
  const baseRef = useRef('');
  const { supported, listening, toggle } = useSpeechRecognition({
    onResult: (transcript, isFinal) => {
      onChange((baseRef.current ? baseRef.current + ' ' : '') + transcript);
      if (isFinal) baseRef.current = (baseRef.current ? baseRef.current + ' ' : '') + transcript;
    },
  });

  const onMic = () => {
    if (!listening) baseRef.current = value;
    toggle();
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (value.trim() && !loading) onSubmit();
    }
  };

  return (
    <div className="glass rounded-xl p-3">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full resize-none bg-transparent px-1 text-content placeholder:text-content-subtle focus:outline-none"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {voice && supported && (
            <button
              type="button"
              onClick={onMic}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors',
                listening
                  ? 'bg-danger/15 text-danger'
                  : 'text-content-subtle hover:bg-white/5 hover:text-content'
              )}
              aria-label={listening ? 'Stop listening' : 'Start voice input'}
            >
              {listening ? (
                <>
                  <MicOff className="h-4 w-4" />
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
                  </span>
                </>
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </button>
          )}
          <span className="hidden text-xs text-content-subtle sm:inline">⌘ + ↵ to generate</span>
        </div>
        <Button
          onClick={onSubmit}
          magnetic
          loading={loading}
          disabled={disabled || !value.trim()}
          icon={loading ? undefined : Sparkles}
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}
